import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, DialogTitle, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { DialogAnimate } from 'src/components/animate';
import { GeneralDatePicker, GeneralSelectMenu } from 'src/components/general';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { SkeletonForm } from 'src/components/skeleton';
import { SOCIAL_MANAGER_CONTENT_LIST } from 'src/constants/queryKeys';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useFetchSocialManagerAccountList } from 'src/network/social-maneger/account';
import { useFetchSocialManagerChannelList } from 'src/network/social-maneger/channel';
import {
  networkCreateSocialManagerContent,
  networkUpdateSocialManagerContent,
  useFetchSocialManagerContentTypeList,
  useFetchSocialManagerContentTypeStatus,
} from 'src/network/social-maneger/content';
import AccountSelectionTableModal from 'src/sections/@dashboard/social-manager/account/AccountSelectionTableModal';
import ChannelSelectionTableModal from 'src/sections/@dashboard/social-manager/channel/ChannelSelectionTableModal';
import useFormsModalStore from 'src/store/forms';
import { generateSelectMenuItems } from 'src/utils';
import { getDefaultValues } from './defaultValues';
import { schema } from './validationSchema';

const SocialManagerContentForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const contentDetails = modal?.data;
  const isEditMode = !!contentDetails;

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [modalMode, setModalMode] = useState('hidden'); // hidden | account | channel
  const [accountDetails, setAccountDetails] = useState(null);
  const [channelDetails, setChannelDetails] = useState(null);

  const [, setSavedPage] = useLocalStorage('savedPage');

  const [accountsTableData, setAccountsTableData] = useState([]);
  const [channelTableData, setChannelTableData] = useState([]);

  const [currentPageAccountsModal, setCurrentPageAccountsModal] = useState(0);
  const [AccountFilterItemsModalPayload] = useState({ TypeID: 0 });

  const [createdDateTime, setCreatedDateTime] = useState(new Date(contentDetails?.SocialManagerContentCreatedDateTime));
  const [updatedDateTime, setUpdatedDateTime] = useState(new Date(contentDetails?.SocialManagerContentStatusUpdatedDateTime));

  const [contentsTypes, setContentsType] = useState([]);
  const [selectedContentType, setSelectedContentType] = useState('');

  const [contentTypesStatus, setContentTypesStatus] = useState([]);
  const [selectedContentTypeStatus, setSelectedContentTypeStatus] = useState('');

  const { isLoading: accountItemsIsLoading, data: accountsListData } = useFetchSocialManagerAccountList(
    AccountFilterItemsModalPayload,
    currentPageAccountsModal
  );
  const { isLoading: channelListIsLoading, data: channelListData } = useFetchSocialManagerChannelList();
  const { isLoading: contentTypeListIsLoading, data: contentTypeListData } = useFetchSocialManagerContentTypeList({ ID: -1 });
  const { isLoading: contentTypesStatusIsLoading, data: contentTypesStatusData } = useFetchSocialManagerContentTypeStatus();

  // ---------------------------------------------------------------

  const defaultValues = getDefaultValues(contentDetails, channelDetails?.SocialManagerChannelID);
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      SocialManagerContentSocialManagerAccountID: +accountDetails?.SocialManagerAccountID,
      SocialManagerContentSocialManagerChannelID: +channelDetails?.SocialManagerChannelID,
      SocialManagerContentSocialManagerContentTypeID: +selectedContentType,
      SocialManagerContentSocialManagerContentStatusTypeID: +selectedContentTypeStatus,
      SocialManagerCreatedDateTime: new Date(createdDateTime).toISOString(),
      SocialManagerStatusUpdatedDateTime: new Date(updatedDateTime).toISOString(),
    };
    try {
      if (isEditMode) {
        const res = await networkUpdateSocialManagerContent(contentDetails?.SocialManagerContentID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('کانال موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries({ queryKey: [SOCIAL_MANAGER_CONTENT_LIST] });
          }
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateSocialManagerContent(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('کانال موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            await queryClient.invalidateQueries({ queryKey: [SOCIAL_MANAGER_CONTENT_LIST] });
          }
          reset();
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const selectAccountHandler = (item) => {
    setModalMode('hidden');
    setAccountDetails(item);
    setValue('SocialManagerContentSocialManagerAccountID', item?.SocialManagerAccountTypeName);
  };
  const selectChannelHandler = (item) => {
    setModalMode('hidden');
    setChannelDetails(item);
    setValue('SocialManagerContentSocialManagerChannelID', item?.SocialManagerChannelName);
  };

  useEffect(() => {
    if (accountsListData) {
      const accountsList = accountsListData?.data?.items || [];

      setAccountsTableData([...accountsList]);
      setSavedPage({
        pageIndex: currentPageAccountsModal,
        pageName: 'accounts-list',
      });

      const findCurrentAccount = accountsList.find(
        (accountItem) => accountItem?.SocialManagerAccountTypeID === contentDetails?.SocialManagerAccountSocialManagerAccountTypeID
      );

      setAccountDetails(findCurrentAccount);
    }
  }, [accountsListData]);

  useEffect(() => {
    if (channelListData) {
      const channelsList = channelListData?.data?.items || [];

      setChannelTableData([...channelsList]);
      setSavedPage({
        pageIndex: currentPageAccountsModal,
        pageName: 'accounts-list',
      });

      const findCurrentChannel = channelsList.find(
        (channelItem) => channelItem?.SocialManagerChannelID === contentDetails?.SocialManagerContentSocialManagerChannelID
      );

      setChannelDetails(findCurrentChannel);
    }
  }, [channelListData]);

  useEffect(() => {
    const contentsType = generateSelectMenuItems(
      contentTypeListData?.data?.items,
      'SocialManagerContentTypeName',
      'SocialManagerContentTypeID'
    );

    const contentTypesStatus = generateSelectMenuItems(
      contentTypesStatusData?.data?.items || [],
      'SocialManagerContentStatusTypeLabel',
      'SocialManagerContentStatusTypeID'
    );

    setContentTypesStatus(contentTypesStatus);
    setContentsType(contentsType);

    setSelectedContentType(contentDetails?.SocialManagerContentTypeID || contentsType?.[0]?.id);
    setSelectedContentTypeStatus(contentDetails?.SocialManagerContentStatusTypeID || contentTypesStatus?.[0]?.id);
  }, [contentTypesStatusIsLoading, contentTypeListIsLoading]);

  useEffect(() => {
    setValue('SocialManagerContentSocialManagerAccountID', accountDetails?.SocialManagerAccountTypeName);
  }, [accountDetails]);

  useEffect(() => {
    setValue('SocialManagerContentSocialManagerChannelID', contentDetails?.SocialManagerChannelName);
  }, [channelDetails]);

  const isShowLoading = accountItemsIsLoading || channelListIsLoading || contentTypeListIsLoading || contentTypesStatusIsLoading;

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            {isShowLoading ? (
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' },
                  width: '100%',
                }}
              >
                <SkeletonForm number={7} />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  <RHFTextField name="SocialManagerContentFileName" type="text" label="نام فایل محتوا" />
                  <RHFTextField name="SocialManagerContentContent" type="text" label="محتوا" />
                  <RHFTextField
                    name="SocialManagerContentSocialManagerAccountID"
                    type="text"
                    label="انتخاب حساب"
                    InputProps={{
                      endAdornment: (
                        <Button variant="contained" color="inherit" onClick={() => setModalMode('account')}>
                          انتخاب
                        </Button>
                      ),
                    }}
                  />
                  <RHFTextField
                    name="SocialManagerContentSocialManagerChannelID"
                    type="text"
                    label="انتخاب کانال"
                    InputProps={{
                      endAdornment: (
                        <Button variant="contained" color="inherit" onClick={() => setModalMode('channel')}>
                          انتخاب
                        </Button>
                      ),
                    }}
                  />
                  <GeneralSelectMenu
                    label="نوع محتوا"
                    name="SocialManagerContentTypeID"
                    options={contentsTypes}
                    value={selectedContentType}
                    onChangeValue={setSelectedContentType}
                  />
                  <GeneralSelectMenu
                    label="وضعیت محتوا"
                    name="SocialManagerContentSocialManagerContentStatusTypeID"
                    options={contentTypesStatus}
                    value={selectedContentTypeStatus}
                    onChangeValue={setSelectedContentTypeStatus}
                  />
                  <GeneralDatePicker
                    label="تاریخ ایجاد"
                    name="SocialManagerCreatedDateTime"
                    value={createdDateTime}
                    onChangeValue={setCreatedDateTime}
                  />
                  <GeneralDatePicker
                    minDate={createdDateTime}
                    label="تاریخ بروزرسانی"
                    name="SocialManagerStatusUpdatedDateTime"
                    value={updatedDateTime}
                    onChangeValue={setUpdatedDateTime}
                  />

                  <RHFTextField name="SocialManagerContentFile" multiline rows={6} type="text" label=" فایل محتوا" className="col-span-2" />
                </Box>
                <div className="flex items-center gap-4 mt-6">
                  <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                    ذخیره
                  </LoadingButton>
                  <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                    {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
                  </LoadingButton>
                  <Button onClick={closeModal} variant="contained" color="error">
                    انصراف
                  </Button>
                </div>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      <DialogAnimate open={modalMode !== 'hidden'} onClose={() => setModalMode('hidden')} maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center', mb: 2 }}>{modalMode === 'account' ? 'انتخاب حساب' : 'انتخاب کانال'}</DialogTitle>
        <div style={{ maxHeight: '80vh' }}>
          {modalMode === 'account' ? (
            <AccountSelectionTableModal
              selectContentHandler={selectAccountHandler}
              setPage={setCurrentPageAccountsModal}
              isLoading={accountItemsIsLoading}
              tableData={accountsTableData}
            />
          ) : modalMode === 'channel' ? (
            <ChannelSelectionTableModal
              selectContentHandler={selectChannelHandler}
              isLoading={channelListIsLoading}
              tableData={channelTableData}
            />
          ) : null}
        </div>
      </DialogAnimate>
    </FormProvider>
  );
};

export default SocialManagerContentForm;
